import React from 'react';
import { Badge, Skeleton, Tooltip } from 'antd';
import { Image as ImageIcon, TriangleAlert } from 'lucide-react';
import { getAuditImageUrl } from '../../../utils/barcodeValidation';
import { brand } from '../../../theme/tokens';

export default function AuditImageThumb({ record, size = 48, onClick }) {
    const status = record?.imageProcessingStatus || 'pending';
    const imageCount = record?.imageCount ?? (Array.isArray(record?.images) ? record.images.length : 0);
    const thumbnailUrl = record?.thumbnailUrl || (record?.images?.[0] ? getAuditImageUrl(record.images[0]) : null);

    // Size is dynamic (48 in tables, 64–72 in cards), so it stays inline.
    const sizeStyle = { width: size, height: size };
    const frame = `relative shrink-0 overflow-hidden rounded-lg ${onClick ? 'cursor-pointer' : 'cursor-default'}`;

    const renderImage = (overlay) => {
        const img = (
            <div style={sizeStyle} onClick={onClick} className={`${frame} border border-surface-200 bg-surface-50`}>
                <img src={thumbnailUrl} alt="" style={sizeStyle} className="object-cover" />
                {overlay}
            </div>
        );

        if (imageCount > 1) {
            return (
                <Badge count={`+${imageCount - 1}`} size="small" offset={[-4, 4]} color={brand[600]}>
                    {img}
                </Badge>
            );
        }
        return img;
    };

    // Show the image whenever we have a displayable URL, even if S3 upload is still pending.
    if (thumbnailUrl && imageCount > 0) {
        const stillProcessing = status === 'pending' || status === 'processing' || status === 'queued';
        const overlay = stillProcessing ? (
            <Tooltip title="Imagen local — subida a la nube en progreso">
                <div className="absolute inset-x-0 bottom-0 bg-warning-solid/90 text-white text-[9px] font-medium text-center leading-tight py-0.5">
                    Subiendo
                </div>
            </Tooltip>
        ) : null;
        return renderImage(overlay);
    }

    if (status === 'pending' || status === 'processing' || status === 'queued') {
        return (
            <Tooltip title="Procesando imágenes">
                <div style={sizeStyle} className={`${frame} flex items-center justify-center border border-warning-border bg-warning-bg`} onClick={onClick}>
                    <Skeleton.Avatar active size={size - 8} shape="square" />
                </div>
            </Tooltip>
        );
    }

    if (status === 'failed') {
        return (
            <Tooltip title={record?.imageProcessingError || 'Error al procesar imágenes'}>
                <div
                    style={sizeStyle}
                    className={`${frame} flex items-center justify-center border border-danger-border bg-danger-bg text-danger-fg text-base`}
                    onClick={onClick}>
                    <TriangleAlert />
                </div>
            </Tooltip>
        );
    }

    if (!thumbnailUrl || imageCount === 0) {
        return (
            <Tooltip title="Sin imagen">
                <div
                    style={sizeStyle}
                    className={`${frame} flex items-center justify-center border border-surface-200 bg-surface-100 text-surface-400 text-base`}
                    onClick={onClick}>
                    <ImageIcon />
                </div>
            </Tooltip>
        );
    }

    return renderImage();
}
